import Axios from 'axios';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { Store } from '../Store';
import { getError } from '../utils';
import { api } from '../Api';
import ConfirmActionModal from '../component/ConfirmActionModal';
import MerchantMoneyRequestsPanel from '../component/MerchantMoneyRequestsPanel';

const DEFAULT_LOGO =
  'https://res.cloudinary.com/dddogyqqa/image/upload/v1670435815/logo/clickcart-logo_wfktlg.png';

/** One point pays out at 12 Tk — the same rule the PDF and the Topup page always used. */
const TK_PER_POINT = 12;

const tk = (n) => `Tk ${Number(n || 0).toLocaleString()}`;
const pt = (n) => `${Number(n || 0).toLocaleString()} pt`;

/**
 * WITHDRAW REQUESTS — the merchant's side.
 *
 * A member can ask for a withdraw paid by a merchant in their upline. Those
 * requests land here: the merchant pays the member outside the system, then
 * approves the request, and the points move to the merchant's Shopping
 * Balance. One at a time, or several at once with one combined PDF.
 *
 * Merchants only — anyone else gets a short note and a way back.
 *
 * A second tab, Request Money, holds the opposite flow: members who paid the
 * merchant offline asking for Shopping Balance (MerchantMoneyRequestsPanel).
 */

const STATUS_TABS = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'paid', label: 'Approved' },
];

function Frame({ onBack, children }) {
  return (
    <div>
      <Helmet>
        <title>Withdraw Requests</title>
      </Helmet>
      <style>{CSS}</style>
      <Container className="no-gutter">
        <div className="card mb-3">
          <Row>
            <Col style={{ textAlign: 'center' }}>
              <img src={DEFAULT_LOGO} style={{ width: '15rem', margin: '2rem' }} alt="ClickCart Logo" />
            </Col>
          </Row>
          <div className="card-header" onClick={onBack} role="button">
            <i className="fa fa-long-arrow-left" aria-hidden="true"></i> {'  '} Withdraw Requests
          </div>
          <div className="card-body tu-body">{children}</div>
        </div>
      </Container>
    </div>
  );
}

export default function MerchantWithdrawRequestsScreen() {
  const { state } = useContext(Store);
  const { userInfo } = state;
  const navigate = useNavigate();
  const auth = useMemo(
    () => ({ headers: { authorization: `Bearer ${userInfo?.token}` } }),
    [userInfo]
  );

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  // `isAssociation` is not in the login payload — the topup settings call
  // reads it fresh from the user record, same as the Topup page did.
  const loadSettings = useCallback(async () => {
    try {
      const { data } = await Axios.get(api + '/api/topup/settings', auth);
      setSettings(data);
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setLoading(false);
    }
  }, [auth]);

  useEffect(() => {
    if (!userInfo) {
      navigate('/signin?redirect=/merchant/withdraw-requests');
      return;
    }
    loadSettings();
  }, [userInfo, navigate, loadSettings]);

  const isAssociation = Boolean(settings?.me?.isAssociation);

  /* --------------------------------------------------- merchant withdraws */

  const [withdrawRequests, setWithdrawRequests] = useState({ rows: [], page: 1, pages: 1, count: 0 });
  const [pendingCount, setPendingCount] = useState(0);
  const [listLoading, setListLoading] = useState(false);
  const [withdrawSelected, setWithdrawSelected] = useState([]);
  const [approvingId, setApprovingId] = useState('');
  const [bulkPaying, setBulkPaying] = useState(false);
  // Every withdraw row seen so far, by id — the selection survives paging, so
  // the summary totals need rows that are no longer on the current page.
  const wdRowCache = useRef({});

  const [wdSearch, setWdSearch] = useState('');
  const [wdStatus, setWdStatus] = useState('pending');
  const [wdFrom, setWdFrom] = useState('');
  const [wdTo, setWdTo] = useState('');
  const [wdFilters, setWdFilters] = useState({ q: '', status: 'pending', from: '', to: '' });
  const wdFiltersActive = wdFilters.q || wdFilters.from || wdFilters.to;

  // Withdraw | Request Money, each tab with its own pending count.
  const [view, setView] = useState('withdraw');
  const [moneyPending, setMoneyPending] = useState(0);

  const loadPendingCount = useCallback(async () => {
    if (!isAssociation) return;
    try {
      const { data } = await Axios.get(
        api + '/api/association/withdraw/mine?status=pending&page=1&pageSize=1',
        auth
      );
      setPendingCount(Number(data.count) || 0);
    } catch (error) {
      /* informational */
    }
    try {
      const { data } = await Axios.get(
        api + '/api/money-requests/merchant?status=pending&page=1&pageSize=1',
        auth
      );
      setMoneyPending(Number(data.count) || 0);
    } catch (error) {
      /* informational */
    }
  }, [auth, isAssociation]);

  const loadWithdrawRequests = useCallback(
    async (page = 1, filters = wdFilters) => {
      if (!isAssociation) return;
      setListLoading(true);
      try {
        const params = new URLSearchParams({ page, pageSize: 10 });
        if (filters.q) params.set('q', filters.q);
        if (filters.status) params.set('status', filters.status);
        if (filters.from) params.set('from', filters.from);
        if (filters.to) params.set('to', filters.to);
        const { data } = await Axios.get(
          api + `/api/association/withdraw/mine?${params.toString()}`,
          auth
        );
        (data.rows || []).forEach((r) => {
          wdRowCache.current[r._id] = r;
        });
        setWithdrawRequests(data);
      } catch (error) {
        toast.error(getError(error));
      } finally {
        setListLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [auth, isAssociation]
  );

  const applyWdFilters = (overrides = {}) => {
    const next = { q: wdSearch.trim(), status: wdStatus, from: wdFrom, to: wdTo, ...overrides };
    setWdFilters(next);
    loadWithdrawRequests(1, next);
  };

  const pickStatus = (status) => {
    setWdStatus(status);
    applyWdFilters({ status });
  };

  const clearWdFilters = () => {
    setWdSearch('');
    setWdFrom('');
    setWdTo('');
    const next = { q: '', status: wdStatus, from: '', to: '' };
    setWdFilters(next);
    loadWithdrawRequests(1, next);
  };

  const refreshAll = () => {
    loadWithdrawRequests(withdrawRequests.page, wdFilters);
    loadPendingCount();
    loadSettings();
  };

  useEffect(() => {
    loadWithdrawRequests(1, wdFilters);
    loadPendingCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAssociation]);

  // Every approval opens the confirm modal first: { kind: 'single', row } or
  // { kind: 'bulk', rows }. The modal is what actually calls the server.
  const [confirm, setConfirm] = useState(null);

  const approveWithdraw = (row) => {
    if (!row || row.status) return;
    setConfirm({ kind: 'single', row });
  };

  const doApproveWithdraw = async (row) => {
    const id = row._id;
    setApprovingId(id);
    try {
      const { data } = await Axios.post(
        api + `/api/association/withdraw/${id}/approve`,
        {},
        auth
      );
      toast.success(data.message);
      setConfirm(null);
      setWithdrawSelected((prev) => prev.filter((x) => x !== id));
      refreshAll();
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setApprovingId('');
    }
  };

  // A merchant approving several requests at once: confirm, approve server-side,
  // then hand them ONE combined PDF of exactly the rows that were just approved.
  const markSelectedPaid = () => {
    const pending = withdrawSelected
      .map((id) => wdRowCache.current[id])
      .filter((w) => w && !w.status);
    if (!pending.length) {
      toast.info('None of the selected requests are pending.');
      return;
    }
    setConfirm({ kind: 'bulk', rows: pending });
  };

  const doMarkSelectedPaid = async (pending) => {
    setBulkPaying(true);
    try {
      const { data } = await Axios.post(
        api + '/api/association/withdraw/approve-bulk',
        { ids: pending.map((w) => w._id) },
        auth
      );
      if (data.approved?.length) {
        const a = document.createElement('a');
        a.href = `${api}/api/invoices/withdraw/bulk.pdf?ids=${data.approved.join(',')}&token=${userInfo?.token}&download=1`;
        a.download = `withdraw-approved-${new Date().toISOString().slice(0, 10)}.pdf`;
        a.rel = 'noreferrer';
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.success(data.message);
      } else {
        toast.warn(data.message);
      }
      if (data.skipped?.length) {
        toast.info(`${data.skipped.length} skipped: ${data.skipped.map((x) => x.reason).filter((v, i, arr) => arr.indexOf(v) === i).join(' ')}`);
      }
      setConfirm(null);
      setWithdrawSelected([]);
      refreshAll();
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setBulkPaying(false);
    }
  };

  const goBack = () => navigate(-1);

  if (loading) {
    return (
      <Frame onBack={goBack}>
        <div className="tu-center tu-muted" style={{ padding: '24px 0' }}>Loading…</div>
      </Frame>
    );
  }

  if (!isAssociation) {
    return (
      <Frame onBack={goBack}>
        <div className="tu-panel tu-center" style={{ padding: '28px 14px' }}>
          <div className="tu-empty-icon">
            <i className="fa fa-lock" aria-hidden="true"></i>
          </div>
          <b>Merchants only</b>
          <div className="tu-muted">
            Withdraw requests are paid by merchants. This page opens once your account is a merchant.
          </div>
          <div style={{ marginTop: 10 }}>
            <button type="button" className="tu-btn tu-btn-light" onClick={() => navigate('/user-panel')}>
              Back to dashboard
            </button>
          </div>
        </div>
      </Frame>
    );
  }

  const pageRows = withdrawRequests.rows || [];
  const pagePoint = pageRows.reduce((sum, w) => sum + Number(w.amount || 0), 0);

  return (
    <Frame onBack={goBack}>
      {/* ---------------------------------------------------- balance banner */}
      <div className="tu-hero">
        <div className="tu-hero-main">
          <span>Shopping balance</span>
          <strong>৳{Number(settings?.me?.shoppingbalance || 0).toLocaleString()}</strong>
          <small>
            {(pendingCount + moneyPending).toLocaleString()} request{pendingCount + moneyPending === 1 ? '' : 's'} waiting on you
          </small>
        </div>
        <span className="tu-chip tu-chip-purple">
          <img src={settings?.me?.associationLogo || DEFAULT_LOGO} alt="" className="tu-assoc-logo" />
          {settings?.me?.associationName || 'Merchant'}
        </span>
      </div>

      <p className="tu-intro">
        Members who chose you as their withdraw merchant appear here. Pay the member the Tk
        amount (points × {TK_PER_POINT}), then approve the request — the same Tk amount is
        credited to your Shopping Balance.
      </p>

      {settings?.me?.associationDisabled ? (
        <div className="tu-bad-banner">
          <i className="fa fa-ban" aria-hidden="true"></i> Your merchant account is disabled by the
          office. You can see your requests, but approving them is blocked until it is enabled again.
        </div>
      ) : null}

      <div className="tu-tabs tu-view-tabs">
        <button
          type="button"
          className={view === 'withdraw' ? 'tu-tab tu-tab-active tu-view-tab' : 'tu-tab tu-view-tab'}
          onClick={() => setView('withdraw')}
        >
          <i className="fa fa-money" aria-hidden="true"></i> Withdraw
          <span className="tu-count">{pendingCount}</span>
        </button>
        <button
          type="button"
          className={view === 'money' ? 'tu-tab tu-tab-active tu-view-tab' : 'tu-tab tu-view-tab'}
          onClick={() => setView('money')}
        >
          <i className="fa fa-hand-o-up" aria-hidden="true"></i> Request Money
          <span className="tu-count">{moneyPending}</span>
        </button>
      </div>

      {view === 'money' ? (
        <MerchantMoneyRequestsPanel
          token={userInfo?.token}
          balance={Number(settings?.me?.shoppingbalance || 0)}
          disabled={Boolean(settings?.me?.associationDisabled)}
          onChanged={() => {
            loadPendingCount();
            loadSettings();
          }}
        />
      ) : null}

      {view === 'withdraw' ? (
      <section className="tu-panel">
        <h6 className="tu-panel-head">
          <i className="fa fa-money" aria-hidden="true"></i> Withdraw requests
          <span className="tu-count">{withdrawRequests.count}</span>
        </h6>

        <div className="tu-tabs">
          {STATUS_TABS.map((t) => (
            <button
              type="button"
              key={t.value || 'all'}
              className={wdFilters.status === t.value ? 'tu-tab tu-tab-green tu-tab-active' : 'tu-tab tu-tab-green'}
              onClick={() => pickStatus(t.value)}
            >
              {t.label}
              {t.value === 'pending' ? <span className="tu-count">{pendingCount}</span> : null}
            </button>
          ))}
        </div>

        <div className="tu-filters">
          <input
            className="tu-input"
            placeholder="Search member username…"
            value={wdSearch}
            onChange={(e) => setWdSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applyWdFilters()}
          />
          <input
            className="tu-input"
            type="date"
            value={wdFrom}
            onChange={(e) => setWdFrom(e.target.value)}
            title="From date"
          />
          <input
            className="tu-input"
            type="date"
            value={wdTo}
            onChange={(e) => setWdTo(e.target.value)}
            title="To date"
          />
          <button type="button" className="tu-btn tu-btn-primary" onClick={() => applyWdFilters()}>
            Search
          </button>
          {wdFiltersActive ? (
            <button type="button" className="tu-btn tu-btn-light" onClick={clearWdFilters}>
              Clear
            </button>
          ) : null}
        </div>

        {pageRows.length > 0 ? (
          <div className="tu-totals">
            <div>
              <b>{withdrawRequests.count.toLocaleString()}</b>
              <span>Matching requests</span>
            </div>
            <div>
              <b>{pagePoint.toLocaleString()} pt</b>
              <span>This page</span>
            </div>
            <div>
              <b>৳{(pagePoint * TK_PER_POINT).toLocaleString()}</b>
              <span>This page (× {TK_PER_POINT})</span>
            </div>
          </div>
        ) : null}

        {listLoading && pageRows.length === 0 ? (
          <div className="tu-center tu-muted" style={{ padding: '18px 0' }}>Loading…</div>
        ) : pageRows.length === 0 ? (
          <div className="tu-center tu-muted" style={{ padding: '18px 0' }}>
            <div className="tu-empty-icon">
              <i className="fa fa-inbox" aria-hidden="true"></i>
            </div>
            {wdFilters.status === 'pending'
              ? 'Nothing waiting on you right now.'
              : wdFilters.status === 'paid'
              ? 'No approved withdraw requests yet.'
              : 'No withdraw requests yet.'}
          </div>
        ) : (
          <>
            {(() => {
              const pageIds = pageRows.filter((w) => !w.status).map((w) => w._id);
              if (!pageIds.length) return null;
              const allOn = pageIds.every((id) => withdrawSelected.includes(id));
              return (
                <label className="tu-wd-selectall">
                  <input
                    type="checkbox"
                    checked={allOn}
                    onChange={(e) =>
                      setWithdrawSelected((prev) =>
                        e.target.checked
                          ? [...new Set([...prev, ...pageIds])]
                          : prev.filter((id) => !pageIds.includes(id))
                      )
                    }
                  />
                  Select all pending on this page ({pageIds.length})
                </label>
              );
            })()}
            {withdrawSelected.length > 0 && (() => {
              const selectedRows = withdrawSelected.map((id) => wdRowCache.current[id]).filter(Boolean);
              const totalPoint = selectedRows.reduce((sum, w) => sum + Number(w.amount || 0), 0);
              const totalTk = totalPoint * TK_PER_POINT;
              const selPending = selectedRows.filter((w) => !w.status).length;
              return (
                <div className="tu-select-summary">
                  <div className="tu-select-stat">
                    <span className="tu-select-num">{withdrawSelected.length}</span>
                    <span className="tu-select-label">Entries selected</span>
                  </div>
                  <div className="tu-select-stat">
                    <span className="tu-select-num">{totalPoint.toLocaleString()}</span>
                    <span className="tu-select-label">Total point</span>
                  </div>
                  <div className="tu-select-stat">
                    <span className="tu-select-num">৳{totalTk.toLocaleString()}</span>
                    <span className="tu-select-label">Total Tk (× {TK_PER_POINT})</span>
                  </div>
                  <a
                    className="tu-btn tu-btn-primary"
                    target="_blank"
                    rel="noreferrer"
                    href={`${api}/api/invoices/withdraw/bulk.pdf?ids=${withdrawSelected.join(',')}&token=${userInfo?.token}&download=1`}
                  >
                    Download as PDF
                  </a>
                  <button
                    type="button"
                    className="tu-btn tu-btn-paid"
                    onClick={markSelectedPaid}
                    disabled={bulkPaying || selPending === 0}
                    title={selPending === 0 ? 'No pending requests selected' : ''}
                  >
                    {bulkPaying ? 'Approving…' : `Approve selected${selPending ? ` (${selPending})` : ''}`}
                  </button>
                  <button
                    type="button"
                    className="tu-btn tu-btn-light"
                    onClick={() => setWithdrawSelected([])}
                  >
                    Clear
                  </button>
                </div>
              );
            })()}
            <div className="tu-wd-list">
              {pageRows.map((w) => {
                const tk = Number(w.amount || 0) * TK_PER_POINT;
                const selected = withdrawSelected.includes(w._id);
                return (
                  <article key={w._id} className={selected ? 'tu-wd-card tu-wd-selected' : 'tu-wd-card'}>
                    <label className="tu-wd-check">
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={(e) =>
                          setWithdrawSelected((prev) =>
                            e.target.checked ? [...prev, w._id] : prev.filter((id) => id !== w._id)
                          )
                        }
                      />
                    </label>
                    <img src={w.profilePhoto || DEFAULT_LOGO} alt="" className="tu-wd-avatar" />
                    <div className="tu-wd-info">
                      <div className="tu-wd-name">{w.name || w.username}</div>
                      <div className="tu-wd-username">@{w.username}</div>
                      <div className="tu-wd-date">
                        <i className="fa fa-clock-o" aria-hidden="true"></i>{' '}
                        {new Date(w.createdAt).toLocaleString()}
                      </div>
                    </div>
                    <div className="tu-wd-amounts">
                      <div className="tu-wd-amount-point">
                        {Number(w.amount).toLocaleString()}<span> pt</span>
                      </div>
                      <div className="tu-wd-amount-tk">৳{tk.toLocaleString()}</div>
                    </div>
                    <div className="tu-wd-side">
                      <span
                        className="tu-chip"
                        style={
                          w.status
                            ? { background: '#dcfce7', color: '#166534' }
                            : { background: '#fef3c7', color: '#92400e' }
                        }
                      >
                        {w.status ? 'Approved' : 'Pending'}
                      </span>
                      <div className="tu-wd-actions">
                        <a
                          className="tu-linkbtn"
                          target="_blank"
                          rel="noreferrer"
                          href={`${api}/api/invoices/withdraw/${w._id}.pdf?token=${userInfo?.token}&download=1`}
                        >
                          PDF
                        </a>
                        {!w.status ? (
                          <button
                            className="tu-linkbtn"
                            onClick={() => approveWithdraw(w)}
                            disabled={approvingId === w._id}
                          >
                            {approvingId === w._id ? 'Approving…' : 'Approve'}
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </>
        )}

        {withdrawRequests.pages > 1 ? (
          <div className="tu-pager">
            <button
              className="tu-btn tu-btn-light"
              disabled={withdrawRequests.page <= 1}
              onClick={() => loadWithdrawRequests(withdrawRequests.page - 1, wdFilters)}
            >
              ‹ Prev
            </button>
            <span className="tu-muted">
              Page {withdrawRequests.page} of {withdrawRequests.pages}
            </span>
            <button
              className="tu-btn tu-btn-light"
              disabled={withdrawRequests.page >= withdrawRequests.pages}
              onClick={() => loadWithdrawRequests(withdrawRequests.page + 1, wdFilters)}
            >
              Next ›
            </button>
          </div>
        ) : null}
      </section>
      ) : null}

      {(() => {
        const balance = Number(settings?.me?.shoppingbalance || 0);
        if (confirm?.kind === 'single') {
          const w = confirm.row;
          const point = Number(w.amount || 0);
          return (
            <ConfirmActionModal
              show
              title="Approve withdraw (mark paid)"
              recipient={{ name: w.name, username: w.username, photo: w.profilePhoto }}
              amount={tk(point * TK_PER_POINT)}
              amountHint={`${pt(point)} × ${TK_PER_POINT} — what you pay the member`}
              rows={[
                { label: 'Member', value: `${w.name || w.username} (@${w.username})` },
                { label: 'Withdraw amount', value: pt(point) },
                { label: 'You pay the member', value: tk(point * TK_PER_POINT) },
                { label: 'Credited to your Shopping Balance', value: `+ ${tk(point * TK_PER_POINT)}`, tone: 'good' },
                { label: 'Your Shopping Balance now', value: tk(balance) },
                { label: 'Your Shopping Balance after', value: tk(balance + point * TK_PER_POINT), strong: true },
              ]}
              note={
                `Pay @${w.username} ${tk(point * TK_PER_POINT)} yourself first. Approving marks this ` +
                `request paid, credits ${tk(point * TK_PER_POINT)} (${pt(point)} × ${TK_PER_POINT}) to your Shopping Balance, ` +
                'and sends the member a "withdrawal paid" message.'
              }
              confirmLabel="Approve"
              busyLabel="Approving…"
              busy={approvingId === w._id}
              onCancel={() => setConfirm(null)}
              onConfirm={() => doApproveWithdraw(w)}
            />
          );
        }
        if (confirm?.kind === 'bulk') {
          const rows = confirm.rows;
          const totalPoint = rows.reduce((sum, w) => sum + Number(w.amount || 0), 0);
          return (
            <ConfirmActionModal
              show
              title={`Approve ${rows.length} withdraw request${rows.length === 1 ? '' : 's'}`}
              amount={tk(totalPoint * TK_PER_POINT)}
              amountHint={`${pt(totalPoint)} × ${TK_PER_POINT} — total you pay out`}
              rows={[
                { label: 'Requests', value: rows.length.toLocaleString() },
                { label: 'Total withdraw', value: pt(totalPoint) },
                { label: 'You pay the members', value: tk(totalPoint * TK_PER_POINT) },
                { label: 'Credited to your Shopping Balance', value: `+ ${tk(totalPoint * TK_PER_POINT)}`, tone: 'good' },
                { label: 'Your Shopping Balance now', value: tk(balance) },
                { label: 'Your Shopping Balance after', value: tk(balance + totalPoint * TK_PER_POINT), strong: true },
              ]}
              note={
                `Pay every member below first. Each request is marked paid, ${tk(totalPoint * TK_PER_POINT)} in total ` +
                'is credited to your Shopping Balance, each member gets a "withdrawal paid" message, ' +
                'and one combined PDF downloads. Any request that is no longer pending is skipped.'
              }
              confirmLabel={`Approve ${rows.length}`}
              busyLabel="Approving…"
              busy={bulkPaying}
              onCancel={() => setConfirm(null)}
              onConfirm={() => doMarkSelectedPaid(rows)}
            >
              <div className="cam-list">
                {rows.map((w) => (
                  <div key={w._id}>
                    <span>
                      {w.name || w.username} <small className="tu-muted">@{w.username}</small>
                    </span>
                    <b>
                      {pt(w.amount)} · {tk(Number(w.amount || 0) * TK_PER_POINT)}
                    </b>
                  </div>
                ))}
              </div>
            </ConfirmActionModal>
          );
        }
        return null;
      })()}
    </Frame>
  );
}

const CSS = `
.tu-body { padding: 12px; display: flex; flex-direction: column; gap: 12px; }
.tu-hero { display: flex; align-items: center; justify-content: space-between; gap: 10px;
  background: linear-gradient(135deg, #42b4e7, #2f8fc4); color: #fff; border-radius: 14px;
  padding: 14px 16px; box-shadow: 0 1px 4px rgba(0,0,0,.15); flex-wrap: wrap; }
.tu-hero-main { display: flex; flex-direction: column; line-height: 1.25; min-width: 0; }
.tu-hero-main span { font-size: 12px; opacity: .9; text-transform: uppercase; letter-spacing: .04em; }
.tu-hero-main strong { font-size: 30px; font-weight: 800; }
.tu-hero-main small { font-size: 12px; opacity: .9; }
.tu-intro { margin: 0; color: #14506e; font-size: 13.5px; line-height: 1.5; }
.tu-panel { background: #fff; border-radius: 14px; padding: 14px; display: flex;
  flex-direction: column; gap: 8px; box-shadow: 0 1px 3px rgba(0,0,0,.12); }
.tu-panel-head { display: flex; align-items: center; gap: 8px; margin: 0 0 4px; padding-bottom: 8px;
  border-bottom: 1px solid #e5e7eb; font-size: 13px; font-weight: 800; text-transform: uppercase;
  letter-spacing: .03em; color: #14506e; }
.tu-panel-head i { color: #42b4e7; }
.tu-count { margin-left: auto; background: #e6f5fd; color: #14506e; border-radius: 999px;
  padding: 1px 9px; font-size: 12px; }
.tu-filters { display: flex; gap: 8px; flex-wrap: wrap; align-items: center;
  margin-bottom: 10px; padding: 10px; background: #f8fafc; border-radius: 10px; }
.tu-filters .tu-input { width: auto; flex: 1 1 160px; min-width: 130px; }
.tu-totals { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-bottom: 6px; }
.tu-totals > div { background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 10px;
  padding: 8px 10px; display: flex; flex-direction: column; line-height: 1.25; min-width: 0; }
.tu-totals b { font-size: 16px; color: #166534; }
.tu-totals span { font-size: 11px; color: #6b7280; text-transform: uppercase; letter-spacing: .03em; }
@media (max-width: 480px) { .tu-totals { grid-template-columns: 1fr; } }
.tu-select-summary { display: flex; align-items: center; gap: 22px; flex-wrap: wrap;
  margin-bottom: 12px; padding: 14px 18px; border-radius: 12px;
  background: linear-gradient(135deg, #0f172a, #1e3a5f); box-shadow: 0 2px 10px rgba(15,23,42,.25); }
.tu-select-stat { display: flex; flex-direction: column; line-height: 1.2; }
.tu-select-num { font-size: 22px; font-weight: 800; color: #fff; letter-spacing: .01em; }
.tu-select-label { font-size: 11px; color: #94a3b8; text-transform: uppercase; letter-spacing: .04em; }
.tu-select-summary > a.tu-btn { margin-left: auto; }
@media (max-width: 600px) {
  .tu-select-summary > .tu-btn { flex: 1 1 100%; margin-left: 0; text-align: center; }
}
.tu-wd-selectall { display: inline-flex; align-items: center; gap: 8px; font-size: 13px;
  color: #334155; margin: 0 0 10px 4px; cursor: pointer; user-select: none; }
.tu-wd-selectall input { width: 16px; height: 16px; cursor: pointer; }
.tu-wd-list { display: flex; flex-direction: column; gap: 10px; }
.tu-wd-card { display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  border: 1.5px solid #e5e7eb; border-radius: 14px; padding: 12px 14px; background: #fff;
  box-shadow: 0 1px 2px rgba(0,0,0,.04); transition: border-color .15s ease, box-shadow .15s ease; }
.tu-wd-card:hover { border-color: #bcd8ea; }
.tu-wd-selected { border-color: #42b4e7; box-shadow: 0 0 0 3px #e6f5fd; }
.tu-wd-check input { width: 18px; height: 18px; cursor: pointer; }
.tu-wd-avatar { width: 46px; height: 46px; border-radius: 50%; object-fit: cover;
  background: #f1f5f9; border: 2px solid #fff; box-shadow: 0 0 0 1px #e5e7eb; }
.tu-wd-info { display: flex; flex-direction: column; min-width: 140px; flex: 1 1 160px; }
.tu-wd-name { font-weight: 800; font-size: 14px; color: #111827; }
.tu-wd-username { font-size: 12px; color: #6b7280; }
.tu-wd-date { font-size: 11px; color: #9ca3af; margin-top: 2px; }
.tu-wd-amounts { display: flex; flex-direction: column; align-items: flex-end; gap: 2px;
  padding: 0 10px; min-width: 110px; }
.tu-wd-amount-point { font-size: 18px; font-weight: 800; color: #14506e; }
.tu-wd-amount-point span { font-size: 11px; font-weight: 700; color: #6b7280; }
.tu-wd-amount-tk { font-size: 15px; font-weight: 800; color: #166534; }
.tu-wd-side { display: flex; flex-direction: column; align-items: flex-end; gap: 6px; }
.tu-wd-actions { display: flex; gap: 8px; }
.tu-tabs { display: flex; gap: 6px; margin-bottom: 4px; flex-wrap: wrap; }
.tu-tab { display: inline-flex; align-items: center; gap: 7px; border: 1.5px solid #e5e7eb;
  background: #f8fafc; color: #374151; border-radius: 999px; padding: 7px 14px;
  font-size: 12.5px; font-weight: 700; cursor: pointer; transition: all .15s ease; }
.tu-tab .tu-count { margin-left: 2px; }
.tu-tab-green { color: #166534; }
.tu-tab-green.tu-tab-active { background: linear-gradient(135deg, #22c55e, #15803d); border-color: #15803d; color: #fff; }
.tu-tab-active .tu-count { background: rgba(255,255,255,.25); color: #fff; }
.tu-view-tabs { margin: 0; }
.tu-view-tab { flex: 1 1 0; justify-content: center; font-size: 14px; padding: 10px 14px; }
.tu-view-tab.tu-tab-active { background: linear-gradient(135deg, #42b4e7, #2f8fc4); border-color: #2f8fc4; color: #fff; }
.tu-tab:not(.tu-tab-active):hover { background: #eef2f7; }
.tu-input { width: 100%; border: 1px solid #cfd8e3; border-radius: 10px; padding: 10px 12px;
  font-size: 14px; box-sizing: border-box; background: #fff; }
.tu-input:focus { outline: 3px solid #cdeafa; border-color: #42b4e7; }
.tu-btn { border-radius: 10px; padding: 10px 14px; font-weight: 700; font-size: 14px;
  cursor: pointer; border: 1px solid #cfd8e3; background: #fff; white-space: nowrap; }
.tu-btn-primary { background: #42b4e7; border-color: #42b4e7; color: #fff; }
.tu-btn-primary:hover:enabled { background: #2f9fd1; }
.tu-btn-primary:disabled { opacity: .6; cursor: not-allowed; }
.tu-btn-light { background: #f1f5f9; }
.tu-btn-paid { background: #16a34a; border-color: #16a34a; color: #fff; }
.tu-btn-paid:hover:enabled { background: #15803d; }
.tu-btn-paid:disabled { opacity: .55; cursor: not-allowed; }
.tu-chip { border-radius: 999px; padding: 3px 10px; font-size: 11px; font-weight: 700;
  display: inline-block; }
.tu-chip-purple { background: #ede9fe; color: #5b21b6; white-space: nowrap;
  display: inline-flex; align-items: center; gap: 6px; }
.tu-assoc-logo { width: 16px; height: 16px; border-radius: 50%; object-fit: cover; }
.tu-muted { color: #6b7280; font-size: 12px; }
.tu-bad-banner { background: #fef2f2; border: 1px solid #fecaca; color: #991b1b; border-radius: 10px;
  padding: 9px 11px; font-size: 13px; }
.tu-center { text-align: center; }
.tu-empty-icon { font-size: 30px; color: #b6d7e8; margin-bottom: 6px; }
.tu-linkbtn { background: none; border: 0; color: #2f8fc4; font-size: 12.5px; font-weight: 700;
  cursor: pointer; padding: 2px 6px; }
.tu-pager { display: flex; align-items: center; justify-content: center; gap: 10px; padding-top: 10px; }
@media (max-width: 480px) { .tu-hero-main strong { font-size: 24px; } }
`;
